import { bindActionCreators } from 'redux';
import {
  compact,
  endsWith,
  flow,
  getOr,
  head,
  includes,
  isEmpty,
  isNull,
  map,
  parseInt,
  replace,
  size,
  some,
  sortBy,
  take,
  throttle,
} from 'lodash/fp';
import { connect } from 'react-redux';
import { CUSTOMER_NEEDS, NEEDS_PRODUCT_TYPE_SLUG, PRODUCT_TYPE } from '@ahmdigital/constants';
import { ProductComparer } from '@ahmdigital/ui';
import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';

import { Customer } from '../../selectors/select-customer/types';
import { INCLUDES_HOSPITAL } from '../../components/pricing-modal/constants';
import { MEDIA_SIZE, PREMIUM_REVIEW_TOGGLE_STYLE } from '../../ahm-constants/ui';
import { MediaDisplay } from '../../styles/styles';
import { ProductType } from '../../types/product';
import { RootState } from '../../reducers';
import { VARIANT } from '../../components/offers/tile/constants';
import buildFinancialProductItemListSchema from '../../components/structured-data/utils/build-financial-product-item-list-schema';
import buildFinancialProductSchema from '../../components/structured-data/utils/build-financial-product-schema';
import buildFinancialProductSourceFromRuntime from '../../components/structured-data/utils/build-financial-product-source-from-runtime';
import constants from '../../ahm-constants';
import CustomerActions, { CustomerActionType } from '../../actions/customer';
import getAvailableProductIndex from './utils/get-closest-available-product-index';
import getCombinedProductUrl from '../../utils/get-combined-product-url';
import getFutureDate from '../../utils/get-future-date';
import getIsExtrasComparerPage from '../../utils/get-is-extras-comparer-page';
import getNewProductLabel from '../../utils/get-new-product-label';
import getRoutePath from '../../utils/get-route-path';
import getUnavailableProductProps from '../../pages/build-your-cover/utils/get-unavailable-product-props';
import hasChoosable60Products from '../../utils/has-choosable-60-products';
import hasChoosableProducts from '../../utils/has-choosable-products';
import isProductBestSeller from '../../utils/is-product-best-seller';
import isProductNew from '../../utils/is-product-new';
import LoadingSpinner from '../../components/loading-spinner';
import NavigationHeader from '../../components/navigation-header';
import OfferBanner from '../../components/offers/banner';
import OfferTile from '../../components/offers/tile';
import PickerDrawer from '../../components/picker-drawer';
import PremiumReviewToggle from '../../components/premium-review-toggle-wrapper';
import PriceDisclaimer, { ElementType } from '../../components/price-disclaimer';
import PriceService from '../../models/price';
import PricingDrawer from '../../components/pricing-drawer';
import ProductComparerActions from '../../actions/product-comparer';
import ProductService from '../../models/product';
import selectCart from '../../selectors/select-cart';
import selectCustomer from '../../selectors/select-customer';
import selectHasPartnerReferrer from '../../selectors/select-has-partner-referrer';
import selectTargetSegment from '../../selectors/select-target-segment';
import StructuredData from '../../components/structured-data';
import styles from './styles.module.sass';
import UrlService from '../../models/url';
import useIsAfterHydration from '../../hooks/use-is-after-hydration';

const StyledDisclaimerWrapper = styled.div`
  width: calc(100% + 2rem);
  margin-left: -1rem;
  margin-bottom: -2rem;
  margin-top: 2rem;
`;

const {
  URLS: {
    SALES: { CHOOSABLE_PICKER, CHOOSE_YOUR_COVER_COMBINED_CHOOSE },
  },
} = constants;

const PRODUCT_LISTING_ALL_URLS = [
  constants.URLS.SALES.PRODUCT_LISTING_HOSPITAL,
  constants.URLS.SALES.PRODUCT_LISTING_EXTRAS,
];

// @ts-expect-error - Automatic, Please fix when editing this file
const getNeedsBasedProductLimit = (needsType, needsProductType) => {
  if (needsType === CUSTOMER_NEEDS.TAX && needsProductType === NEEDS_PRODUCT_TYPE_SLUG.HOSPITAL) {
    return 3;
  }
  return null;
};

const RADIX = 10;

const productMapperFactory =
  ({
    // @ts-expect-error - Automatic, Please fix when editing this file
    actions,
    // @ts-expect-error - Automatic, Please fix when editing this file
    cartProduct,
    // @ts-expect-error - Automatic, Please fix when editing this file
    customer,
    // @ts-expect-error - Automatic, Please fix when editing this file
    frequency,
    // @ts-expect-error - Automatic, Please fix when editing this file
    isCombiningHospitalWithExtras,
    // @ts-expect-error - Automatic, Please fix when editing this file
    prices,
    // @ts-expect-error - Automatic, Please fix when editing this file
    scale,
    // @ts-expect-error - Automatic, Please fix when editing this file
    showBestSellerLabel,
    // @ts-expect-error - Automatic, Please fix when editing this file
    state,
    // @ts-expect-error - Automatic, Please fix when editing this file
    useFutureEffectiveDate,
  }) =>
  // @ts-expect-error - Automatic, Please fix when editing this file
  (product) => {
    const isAvailable = ProductService.isAvailableIn(product, state) && ProductService.isAvailableFor(product, scale);
    if (!isAvailable) {
      return null;
    }
    const { id: productId, name: productName, parentProductId, selectButton, type: productType } = product;
    const date = getFutureDate(useFutureEffectiveDate);
    const price = isAvailable ? PriceService.getPrice(prices, productId, customer, { date }) : undefined;
    const priceObject = price ? PriceService.getAmountAsObject(price, frequency) : undefined;

    const showNewProductLabel = isProductNew(productId, parentProductId, productType);
    const newProductLabel = getNewProductLabel();

    return {
      ...product,
      customLabel: showNewProductLabel ? newProductLabel : undefined,
      isAvailable,
      isBestSeller:
        !showNewProductLabel &&
        showBestSellerLabel &&
        isProductBestSeller({
          parentProductId,
          productId,
          productType,
          scale,
        }),
      price: priceObject,
      scale,
      selectButton: selectButton || {
        analyticsLabel: `View ${productName} cover`,
        text: `${isCombiningHospitalWithExtras ? 'Add' : 'View'} cover`,

        to: isCombiningHospitalWithExtras
          ? getCombinedProductUrl({ cartProduct, chosenProduct: product })
          : UrlService.getProductUrl(productId),
      },
      unavailableWithExcessInfo: getUnavailableProductProps(
        actions?.customer.changeHospitalExcess,
        customer.hospitalExcess,
        product,
      ),
    };
  };

type ProductPanelsProps = {
  actions: {
    customer: {
      changeHospitalExcess: CustomerActionType['customer']['changeHospitalExcess'];
    };
    drawer?: {
      openDrawer: (...args: unknown[]) => unknown;
    };
    productComparer: {
      reviewDirectComparisonProductIds: (...args: unknown[]) => unknown;
      storeActiveIndex: (...args: unknown[]) => unknown;
      toggleDirectComparisonProductId: (...args: unknown[]) => unknown;
    };
  };
  activeIndex?: number;
  cart?: {
    product: {};
  };
  comparerProducts: ProductType[];
  customer: Customer;
  isCombiningHospitalWithExtras?: boolean;
  isPartnerReferral: boolean;
  isRankSorted?: boolean;
  match: {
    params?: {
      needsProductType?: string;
      needsType?: string;
    };
  };
  needsProductType?: string;
  needsType?: string;
  pageHistory?: string;
  prices: {};
  productComparisonId: string;
  productIdsWithExcess?: Record<string, number[]> | null;
  productType: string;
  selectedComparisonProductIds: {
    [PRODUCT_TYPE.EXTRAS]?: string[];
    [PRODUCT_TYPE.HOSPITAL]?: string[];
    [PRODUCT_TYPE.PACKAGE]?: string[];
  };
  showBestSellerLabel?: boolean;
  targetSegment: string;
  useFutureEffectiveDate: boolean;
};

const ProductPanels = ({
  actions,
  activeIndex,
  cart,
  comparerProducts,
  customer,
  isCombiningHospitalWithExtras,
  isPartnerReferral,
  isRankSorted,
  match,
  needsProductType,
  needsType,
  prices,
  productComparisonId,
  productIdsWithExcess = null,
  productType,
  selectedComparisonProductIds,
  showBestSellerLabel,
  targetSegment,
  useFutureEffectiveDate,
}: ProductPanelsProps) => {
  const router = useRouter();

  const [isComparisonDrawerOpen, setIsComparisonDrawerOpen] = useState(false);
  const onGoToIndex = throttle(100, (index) => actions.productComparer.storeActiveIndex(index, productComparisonId));
  const cartProduct = cart?.product;
  const comparerProductsType = head(comparerProducts)?.type;
  // @ts-expect-error - Automatic, Please fix when editing this file
  const selectedComparisonProductIdsForType = selectedComparisonProductIds[comparerProductsType];

  // @ts-expect-error - Automatic, Please fix when editing this file
  const toggleComparisonSelections = (productId) => {
    if (includes(productId, selectedComparisonProductIdsForType)) {
      setIsComparisonDrawerOpen(false);
    } else {
      setIsComparisonDrawerOpen(size(selectedComparisonProductIdsForType) === 1);
    }
    actions.productComparer.toggleDirectComparisonProductId(productId, comparerProductsType);
  };

  const toggleComparisonDrawer = () => {
    setIsComparisonDrawerOpen(!isComparisonDrawerOpen);
  };

  const { frequency, scale, state } = customer;

  const mappedProducts = useMemo(() => {
    const sortedProducts = isRankSorted ? sortBy('rank', comparerProducts) : comparerProducts;

    return flow(
      map(
        productMapperFactory({
          actions,
          cartProduct,
          customer,
          frequency,
          isCombiningHospitalWithExtras,
          prices,
          scale,
          showBestSellerLabel,
          state,
          useFutureEffectiveDate,
        }),
      ),
      compact,
    )(sortedProducts);
  }, [
    cartProduct,
    customer,
    actions,
    frequency,
    isCombiningHospitalWithExtras,
    prices,
    scale,
    showBestSellerLabel,
    state,
    useFutureEffectiveDate,
    comparerProducts,
    isRankSorted,
  ]);

  const needsBasedProductLimit = getNeedsBasedProductLimit(needsType, needsProductType);
  const products = needsBasedProductLimit ? take(needsBasedProductLimit, mappedProducts) : mappedProducts;

  const productsLoaded = !isEmpty(products);

  useEffect(() => {
    actions.productComparer.reviewDirectComparisonProductIds(mappedProducts, comparerProductsType);
  }, [cartProduct, comparerProducts]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (size(products)) {
      const currentIndex = !isNull(activeIndex) ? activeIndex : 0;
      const customerExcess = parseInt(RADIX, customer?.hospitalExcess);
      // Reset index back to 0 when toggling between recommended products
      const goToIndex = getAvailableProductIndex({ currentIndex, customerExcess, products });
      onGoToIndex(goToIndex);
    }
  }, [products]); // eslint-disable-line react-hooks/exhaustive-deps

  const isProductWithHospitalServices = some((product) => includes(product.type, INCLUDES_HOSPITAL), products);

  const isProductIdsWithExcessLoaded =
    !isProductWithHospitalServices || !isEmpty(productIdsWithExcess) || isNull(productIdsWithExcess);

  // This prevents them from finishing loading at different times, causing the loading component to flicker.
  const isLoadingVariableExcess = !(productsLoaded && isProductIdsWithExcessLoaded);

  // Must be called before any early return to satisfy rules-of-hooks.
  const isAfterHydration = useIsAfterHydration();

  if (isLoadingVariableExcess || isEmpty(products)) {
    return <LoadingSpinner />;
  }

  const comparerBanners = {
    choosableBanner: {
      link: {
        question: 'Not for you?',
        text: hasChoosableProducts(products) ? 'View more choosable extras' : 'View choosable extras',
        to: CHOOSABLE_PICKER,
      },
    },
    softBundleBanner: {
      link: {
        question: 'Not the right fit?',
        size: 'large',
        text: 'Choose your own hospital & extras',
        to: CHOOSE_YOUR_COVER_COMBINED_CHOOSE,
      },
    },
  };

  const isApplicableForExtrasBanner =
    !hasChoosable60Products(products) && getIsExtrasComparerPage({ needsProductType, needsType });
  const isApplicableForSoftBundleBanner = needsType === CUSTOMER_NEEDS.PACKAGE_COVER;
  let comparerBannerProps = null;
  if (isApplicableForExtrasBanner) {
    comparerBannerProps = comparerBanners.choosableBanner;
  } else if (isApplicableForSoftBundleBanner) {
    comparerBannerProps = comparerBanners.softBundleBanner;
  }

  const routePath = getRoutePath(router.asPath);

  const isProductListingPage = includes(routePath, PRODUCT_LISTING_ALL_URLS);
  const isProductListingPageAll = isProductListingPage && endsWith('/all', routePath);

  const financialProductSchemas = products.map((product) =>
    buildFinancialProductSchema(
      buildFinancialProductSourceFromRuntime({
        product: product as ProductType,
      }),
    ),
  );

  const financialProductItemListSchema = buildFinancialProductItemListSchema(financialProductSchemas);

  const priceDisclaimerProps = {
    bold: true,
    elementType: 'button' as ElementType,
    forProducts: products,
    highlightIncome: true,
    showRefineLink: true,
  };

  const priceDisclaimer = !isProductListingPageAll && (
    <>
      <PriceDisclaimer {...priceDisclaimerProps} hideForDesktop={isProductListingPage} />
      <MediaDisplay media={MEDIA_SIZE.MD}>
        <StyledDisclaimerWrapper>
          <PremiumReviewToggle type={PREMIUM_REVIEW_TOGGLE_STYLE.FLAT_WHITE} showBackground={false} />
        </StyledDisclaimerWrapper>
      </MediaDisplay>
    </>
  );

  return (
    <>
      <StructuredData data={financialProductItemListSchema} />
      <OfferBanner filterProductId={head(products)?.id} targetSegment={targetSegment} variant="sideBordered" />
      <NavigationHeader
        isCombiningHospitalWithExtras={isCombiningHospitalWithExtras}
        productQuantity={size(products)}
        productType={productType}
        totalProducts={comparerProducts.length}
        needsProductType={needsProductType}
        needsType={needsType}
        useHeading
        products={products}
        isProductListingPage={isProductListingPage}
        isProductListingPageAll={isProductListingPageAll}
      />
      <ProductComparer
        // @ts-expect-error [🤖 PLEASE FIX 🤖]: Type 'number | undefined' is not assignable to type 'number'.
        activeIndex={activeIndex}
        // @ts-expect-error [🤖 PLEASE FIX 🤖]: Type '{ link: { question: string; text: string; to: any; }; } | null' is not assignable to type '{ link: { onClick?: ((...args: unknown[]) => unknown) | undefined; question?: string | undefined; size?: string | undefined; text?: string | undefined; to?: string | undefined; }; } | undefined'.
        comparerBanner={comparerBannerProps}
        disclaimer={priceDisclaimer}
        limitWidth
        onGoToIndex={onGoToIndex}
        paymentFrequency={customer.frequency}
        hideBestSeller={!isAfterHydration} // best seller tag isn't hydration safe yet.
        products={products}
        selectedComparisonProductIds={selectedComparisonProductIdsForType}
        toggleComparisonDrawer={toggleComparisonDrawer}
        toggleComparisonSelections={toggleComparisonSelections}
      />
      <PricingDrawer isProductWithHospitalServices={isProductWithHospitalServices} match={match} />
      <PickerDrawer />
      {!isPartnerReferral && (
        <div className={styles.offerTile} data-testid="offer-tile-wrapper">
          <OfferTile
            filterProductId={head(products)?.id}
            // @ts-expect-error - Automatic, Please fix when editing this file
            targetSegment={targetSegment}
            variant={VARIANT.STRIP}
          />
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  // Note: pathname will be undefined when server-side rendering

  const pathname = state.router.location?.pathname;
  const productComparisonId = replace('/health-insurance/', '', pathname);

  return {
    activeIndex: getOr(null, `productComparer.activeIndices.${productComparisonId}`, state),
    cart: selectCart(state),
    customer: selectCustomer(state),
    isPartnerReferral: selectHasPartnerReferrer(state),
    pageHistory: getOr(null, 'history[1]', state),
    productComparisonId,
    selectedComparisonProductIds: state.productComparer?.directComparisonProductIds,
    showBestSellerLabel: some(
      (productListingUrl) => productListingUrl === pathname,
      [
        constants.URLS.SALES.PRODUCT_LISTING_EXTRAS,
        constants.URLS.SALES.PRODUCT_LISTING_EXTRAS_ADD,
        constants.URLS.SALES.PRODUCT_LISTING_HOSPITAL,
        constants.URLS.SALES.PRODUCT_LISTING_HOSPITAL_ADD,
      ],
    ),
    targetSegment: selectTargetSegment(state),
    useFutureEffectiveDate: state.price.useFutureEffectiveDate,
  };
};

// @ts-expect-error - Automatic, Please fix when editing this file
const mapDispatchToProps = (dispatch) => ({
  actions: {
    customer: bindActionCreators(CustomerActions, dispatch),
    productComparer: bindActionCreators(ProductComparerActions, dispatch),
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductPanels);

export { getNeedsBasedProductLimit, ProductPanels as ProductPanelsForTest, mapStateToProps };
